import { DestroyRef, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';

import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { CurrentUserService } from '~shared/services/current-user/current-user.service';
import { LocaleService } from '~shared/services/locale.service';
import { TOKEN_STORAGE_KEY } from '~shared/services/auth/auth-storage.constants';
import {
  clearImpersonationSession,
  isImpersonationSession,
  readStoredToken,
  storeSessionToken,
} from '~shared/services/auth/auth-storage.util';
import { isTokenUnexpired } from '~shared/services/auth/token-validity.util';
import { AuthTabSyncService } from '~shared/services/auth/tab-sync/auth-tab-sync.service';

/**
 * Payload du JWT. Depuis le refactor du permission bundle, le token ne porte
 * plus les rôles (`roles`) : ils sont chargés depuis l'API à chaque besoin.
 * Le JWT ne véhicule que l'identité.
 */
export interface JwtTokenPayload {
  iat: number;
  exp: number;
  username: string;
  ip: string;
  uid: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private router = inject(Router);
  private currentUserService = inject(CurrentUserService);
  private translate = inject(TranslateService);
  private localeService = inject(LocaleService);
  private tabSync = inject(AuthTabSyncService);

  constructor() {
    // Déconnexion dans un autre onglet : on ferme la session ici aussi, sans
    // la rediffuser (sinon les onglets se la renverraient indéfiniment).
    const subscription = this.tabSync.remoteLogout$.subscribe(() =>
      this.endSession()
    );
    inject(DestroyRef).onDestroy(() => subscription.unsubscribe());
  }

  isLoggedIn(): boolean {
    return isTokenUnexpired(this.getJwtToken());
  }

  saveJwtToken(token: string, remember: boolean) {
    if (remember) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      storeSessionToken(token);
    }
  }

  decodeToken() {
    try {
      const token = this.getJwtToken();
      const decodedToken: any = jwtDecode(token as string);
      return decodedToken;
    } catch (error) {
      console.error('Erreur lors du décodage du token :', error);
    }
  }

  /**
   * Déconnexion explicite : ferme la session puis la diffuse aux autres
   * onglets. Un onglet de prise d'identité ne ferme que la sienne et ne
   * diffuse rien : la session de l'utilisateur réel reste ouverte ailleurs.
   */
  logout() {
    const isImpersonating = isImpersonationSession();
    this.endSession();
    if (!isImpersonating) {
      this.tabSync.broadcastLogout();
    }
  }

  private endSession() {
    this.currentUserService.clear();
    this.clearStoredTokens();
    // Token purgé : on recharge le catalogue i18n pour repasser au public seul.
    // Sans cela, ngx-translate conserverait en cache les overrides privés du
    // compte déconnecté (le loader n'ajoute /me/translation que si un token est
    // présent en storage).
    this.translate.reloadLang(this.localeService.getLanguage()).subscribe();
    this.router.navigate(['/login']);
  }

  getJwtToken() {
    return readStoredToken();
  }

  getUserId() {
    const token = this.decodeToken();
    return token ? token.uid : null;
  }

  getJwtTokenPayload(): JwtTokenPayload | null {
    const token = this.getJwtToken();
    if (!token) {
      return null;
    }
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  }

  clearStoredTokens() {
    // Onglet de prise d'identité : seule cette session est close, celle de
    // l'utilisateur réel (autres onglets) reste ouverte.
    if (isImpersonationSession()) {
      clearImpersonationSession();
      return;
    }
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    // Purge des anciennes clés de préférences GLOBALES (non scopées par uid),
    // désormais remplacées par des clés `…:<uid>`. On ne touche PAS aux clés
    // scopées : chaque utilisateur conserve ses préférences entre sessions.
    localStorage.removeItem('dashboardId');
    localStorage.removeItem('last_views_v1');
  }

  /**
   * Admin flag comes from `/me.user.is_admin` (set server-side based on
   * ROLE_CLIENT_ADMIN). The JWT no longer carries roles, so we rely on
   * CurrentUserService which holds the latest /me payload.
   */
  isAdmin(): Observable<boolean> {
    return this.currentUserService.isAdmin();
  }

  /**
   * True if the current user has any CRUD permission on a system table.
   * The sidebar uses this to show/hide the Admin menu.
   */
  hasAnySystemPermission(): boolean {
    return this.currentUserService.hasAnySystemPermission();
  }
}
